import { useEffect, useRef } from 'react';

import {
  getTypeOrNull,
  useCreatePresenceMutation,
  useDeletePresenceMutation,
  useUpdatePresenceMutation,
} from '@spotify/confidence-graphql';

const DEFAULT_HEARTBEAT_INTERVAL_MS = 20_000;

export type UsePresenceHeartbeatOptions = {
  enabled?: boolean;
  intervalMs?: number;
};

// Presence is best-effort: transient failures are swallowed so a flaky
// connection never breaks the page.
export const usePresenceHeartbeat = ({
  enabled = true,
  intervalMs = DEFAULT_HEARTBEAT_INTERVAL_MS,
}: UsePresenceHeartbeatOptions = {}): void => {
  const [createPresence] = useCreatePresenceMutation();
  const [updatePresence] = useUpdatePresenceMutation();
  const [deletePresence] = useDeletePresenceMutation();

  const presenceNameRef = useRef<string | null>(null);
  const presenceIdRef = useRef(
    `web-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  );

  useEffect(() => {
    if (!enabled) return undefined;

    const register = async (): Promise<string | null> => {
      try {
        const result = await createPresence({
          variables: { presenceId: presenceIdRef.current, presence: {} },
        });
        const presence = getTypeOrNull(result.data?.createPresence);
        if (presence) {
          presenceNameRef.current = presence.name;
          return presence.name;
        }
      } catch {
        // Presence is best-effort.
      }
      return null;
    };

    const beat = async () => {
      const name = presenceNameRef.current;
      if (!name || document.visibilityState !== 'visible') return;
      try {
        await updatePresence({
          variables: { presence: { name }, allowMissing: true },
        });
      } catch {
        // Ignore transient heartbeat failures.
      }
    };

    const registration = register();
    const heartbeat = setInterval(() => void beat(), intervalMs);

    return () => {
      clearInterval(heartbeat);
      // Chain off registration so a presence created after a fast unmount is
      // still deleted, rather than lingering until the backend TTL expires.
      // A hard tab/browser close won't run this cleanup, so the backend TTL
      // remains the ultimate backstop.
      void registration.then(registeredName => {
        const name = registeredName ?? presenceNameRef.current;
        presenceNameRef.current = null;
        if (name) {
          void deletePresence({ variables: { name } });
        }
      });
    };
  }, [createPresence, deletePresence, updatePresence, enabled, intervalMs]);
};
